import { useTranslation } from "react-i18next";
// src/pages/CustomerDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import { apiDelete, apiGet, apiPut } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";

// ✅ Apple Standard (your actual exports)
import { A } from "../styles/appleClasses";

type Customer = {
  id: number;
  name: string;
  email?: string | null;
  mobile?: string | null;
  contact?: string | null;
  address?: string | null;
};

type Draft = {
  name: string;
  email: string;
  mobile: string;
  contact: string;
  address: string;
};

function cleanStr(s: string) {
  return String(s ?? "").trim();
}

function dashIfEmpty(v?: string | null) {
  return v && String(v).trim() ? String(v) : "—";
}

type DisplayError = { local: "nameRequired" | "missingId" | "loadDetailFailed" | "saveFailed" | "deleteFailed" } | { raw: string } | null;

export default function CustomerDetail() {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    nameRequired: t("directory.customers.nameRequired"),
    missingId: t("directory.customers.missingId"),
    loadDetailFailed: t("directory.customers.loadDetailFailed"),
    saveFailed: t("directory.customers.saveFailed"),
    deleteFailed: t("directory.customers.deleteFailed"),
  })[error.local];

  const params = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();

  const company_id = params.company_id;

  // tolerate different route param names to prevent “stuck loading”
  const routeId =
    params.id ?? params.customer_id ?? params.customerId ?? params.customerID;

  const listPath = useMemo(
    () => (company_id ? companyRoute(company_id, "/customers") : "/customers"),
    [company_id]
  );

  const withCompany = (path: string) =>
    company_id ? `${path}?company_id=${encodeURIComponent(company_id)}` : path;

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<DisplayError>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    name: "",
    email: "",
    mobile: "",
    contact: "",
    address: "",
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<DisplayError>(null);

  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<DisplayError>(null);

  // --- composed classes (NO cx)
  const PAGE_W = "max-w-xl";
  const iconBtnCls = [
    A.iconBtn,
    "inline-flex items-center justify-center",
    "hover:bg-gray-100",
    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
  ].join(" ");

  const textareaCls = [
    "w-full rounded-xl px-4 py-2",
    "text-[13px] font-normal text-gray-700 placeholder:text-gray-400",
    "border border-gray-200 bg-white transition",
    "focus:outline-none focus:ring-2 focus:ring-appleblue/20 focus:border-appleblue/50",
    "disabled:opacity-60",
  ].join(" ");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!routeId) {
        setLoadError({ local: "missingId" });
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      try {
        const data = await apiGet<Customer>(
          withCompany(`/customers/${routeId}`)
        );
        if (cancelled) return;

        setCustomer(data);
        setDraft({
          name: data.name ?? "",
          email: data.email ?? "",
          mobile: data.mobile ?? "",
          contact: data.contact ?? "",
          address: data.address ?? "",
        });
      } catch (e: any) {
        const msg =
          e?.response?.data?.detail || e?.message;
        if (!cancelled) setLoadError(msg ? { raw: msg } : { local: "loadDetailFailed" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [routeId, company_id]); // company_id affects withCompany()

  const beginEdit = () => {
    if (!customer) return;
    setSaveError(null);
    setDraft({
      name: customer.name ?? "",
      email: customer.email ?? "",
      mobile: customer.mobile ?? "",
      contact: customer.contact ?? "",
      address: customer.address ?? "",
    });
    setEditing(true);
  };

  async function handleSave() {
    if (saving || !customer) return;

    setSaving(true);
    setSaveError(null);

    const payload = {
      name: cleanStr(draft.name),
      email: cleanStr(draft.email) ? cleanStr(draft.email) : null,
      mobile: cleanStr(draft.mobile) ? cleanStr(draft.mobile) : null,
      contact: cleanStr(draft.contact) ? cleanStr(draft.contact) : null,
      address: cleanStr(draft.address) ? cleanStr(draft.address) : null,
    };

    if (!payload.name) {
      setSaving(false);
      setSaveError({ local: "nameRequired" });
      return;
    }

    try {
      await apiPut(withCompany(`/customers/${customer.id}`), payload);
      setCustomer((prev) => (prev ? { ...prev, ...payload } : prev));
      setEditing(false);
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail || e?.message;
      setSaveError(msg ? { raw: msg } : { local: "saveFailed" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (deleting || !customer) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await apiDelete(withCompany(`/customers/${customer.id}`));
      navigate(listPath, { replace: true });
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail || e?.message;
      setDeleteError(msg ? { raw: msg } : { local: "deleteFailed" });
    } finally {
      setDeleting(false);
    }
  }

  const header = (
    <div
      className={[
        "sticky top-0 z-30 bg-gray-50 w-full",
        PAGE_W,
        "flex items-center border-b border-gray-100 px-2 pb-2 mb-2",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        className={iconBtnCls}
        aria-label={t("directory.common.back")}
      >
        <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
      </button>

      <h2 className={`flex-1 text-center tracking-tight ${A.title}`}>
        {t("directory.customers.detail")}
      </h2>

      <button
        type="button"
        onClick={() => navigate(listPath)}
        className={iconBtnCls}
        aria-label={t("directory.common.close")}
      >
        <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 pb-10 px-2 ${A.body}`}
      >
        {header}
        <div className={`w-full ${PAGE_W}`}>
          <div className={`${A.card} p-5 sm:p-6 text-gray-500`}>{t("directory.common.loading")}</div>
        </div>
      </div>
    );
  }

  if (loadError || !customer) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 pb-10 px-2 ${A.body}`}
      >
        {header}
        <div className={`w-full ${PAGE_W}`}>
          <div className={`${A.card} p-5 sm:p-6 text-red-500`}>
            {errorText(loadError) ?? t("directory.customers.notFound")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 pb-10 px-2 ${A.body}`}
    >
      {header}

      <div className={`w-full ${PAGE_W}`}>
        {/* Main Card */}
        <div className={`${A.card} p-5 sm:p-6`}>
          {/* Hero */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className={`${A.title} truncate`}>{customer.name}</div>
              <div className="text-[13px] text-gray-500 truncate">
                {customer.email?.trim() ? customer.email : t("directory.common.noEmail")}
              </div>
            </div>
          </div>

          <div className="h-px bg-gray-100 my-4" />

          {/* Content */}
          {editing ? (
            <div className="space-y-3">
              <Field label={t("directory.common.name")}>
                <input
                  className={A.inputBase}
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, name: e.target.value }))
                  }
                  autoFocus
                  disabled={saving}
                />
              </Field>

              <Field label={t("directory.common.email")}>
                <input
                  className={A.inputBase}
                  value={draft.email}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, email: e.target.value }))
                  }
                  disabled={saving}
                />
              </Field>

              <Field label={t("directory.common.mobile")}>
                <input
                  className={A.inputBase}
                  value={draft.mobile}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, mobile: e.target.value }))
                  }
                  disabled={saving}
                />
              </Field>

              <Field label={t("directory.common.contact")}>
                <input
                  className={A.inputBase}
                  value={draft.contact}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, contact: e.target.value }))
                  }
                  disabled={saving}
                />
              </Field>

              <Field label={t("directory.common.address")}>
                <textarea
                  className={textareaCls}
                  rows={3}
                  value={draft.address}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, address: e.target.value }))
                  }
                  disabled={saving}
                />
              </Field>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <div className="divide-y divide-gray-100">
                <InfoRow label={t("directory.common.name")} value={dashIfEmpty(customer.name)} />
                <InfoRow label={t("directory.common.email")} value={dashIfEmpty(customer.email)} />
                <InfoRow label={t("directory.common.mobile")} value={dashIfEmpty(customer.mobile)} />
                <InfoRow
                  label={t("directory.common.contact")}
                  value={dashIfEmpty(customer.contact)}
                />
                <InfoRow
                  label={t("directory.common.address")}
                  value={dashIfEmpty(customer.address)}
                  multiline
                />
              </div>
            </div>
          )}

          {/* Card footer actions */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            {/* Error slot (right aligned, no layout jump) */}
            <div className="min-h-[18px] text-right">
              {saveError ? (
                <div className="text-xs text-red-500 font-medium">
                  {errorText(saveError)}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-3">
              {!editing ? (
                <>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={beginEdit}
                    className="border-appleblue text-appleblue hover:bg-appleblue/5 focus:outline-none focus:ring-2 focus:ring-appleblueD"
                  >
                    {t("directory.common.edit")}
                  </Button>

                  {/* Delete defaults to secondary/gray */}
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setShowDelete(true);
                      setDeleteError(null);
                    }}
                    className="border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    {t("directory.common.delete")}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setSaveError(null);
                    }}
                    disabled={saving}
                  >
                    {t("directory.common.cancel")}
                  </Button>

                  <Button
                    variant="primary"
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !draft.name.trim()}
                  >
                    {saving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDelete ? (
        <ConfirmDelete
          title={t("directory.customers.deleteConfirm")}
          subtitle={t("directory.common.irreversible")}
          busy={deleting}
          error={errorText(deleteError)}
          onCancel={() => {
            setShowDelete(false);
            setDeleteError(null);
          }}
          onConfirm={handleDelete}
        />
      ) : null}
    </div>
  );
}

function InfoRow(props: { label: string; value: string; multiline?: boolean }) {
  const isDash = props.value === "—";
  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-3">
      <div className="col-span-4 text-xs text-gray-500">{props.label}</div>
      <div
        className={[
          "col-span-8 text-[13px] text-gray-700",
          props.multiline ? "whitespace-pre-line" : "truncate",
          isDash ? " text-gray-400 italic" : "",
        ].join(" ")}
      >
        {props.value}
      </div>
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">{props.label}</div>
      {props.children}
    </div>
  );
}

function ConfirmDelete(props: {
  title: string;
  subtitle: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-3">
      <div className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-md p-4">
        <div className="text-[15px] font-semibold text-gray-700">
          {props.title}
        </div>
        <div className="text-[13px] text-gray-600 mt-1">{props.subtitle}</div>

        <div className="min-h-[18px] mt-2">
          {props.error ? (
            <div className="text-xs text-red-500 font-medium">
              {props.error}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={props.onCancel}
            disabled={props.busy}
          >
            {t("directory.common.cancel")}
          </Button>

          {/* confirm delete is red solid */}
          <Button
            variant="primary"
            type="button"
            onClick={props.onConfirm}
            disabled={props.busy}
            className="bg-red-600 hover:bg-red-700"
          >
            {props.busy ? t("directory.common.deleting") : t("directory.common.delete")}
          </Button>
        </div>
      </div>
    </div>
  );
}
